import React, { useState, useEffect } from "react";
import "./CoursePopUp.css"; // Reuse existing CSS

const CoursePopUpDuplicate = ({ onClose, onSubmit, isLoading, course }) => {
  const [courseName, setCourseName] = useState(
    `${course?.courseName || ""} COPY`
  );
  const [selectedSections, setSelectedSections] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize with all sections selected
  useEffect(() => {
    if (course && course.sections && Array.isArray(course.sections)) {
      const allSectionIds = course.sections.map((section) => section.id);
      setSelectedSections(allSectionIds);
    }
  }, [course]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (currentStep === 2) {
      if (!courseName.trim()) {
        alert("Course name cannot be empty");
        return;
      }

      setIsSubmitting(true);
      onSubmit({
        courseName: courseName.trim(),
        selectedSectionIds: selectedSections,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && currentStep === 1) {
      e.preventDefault();
      goToNextStep();
    }
  };

  const goToNextStep = () => {
    if (currentStep === 1) {
      if (!courseName.trim()) {
        alert("Course name cannot be empty");
        return;
      }
      setCurrentStep(2);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const toggleSectionSelection = (sectionId) => {
    setSelectedSections((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id !== sectionId);
      } else {
        return [...prev, sectionId];
      }
    });
  };

  const selectAllSections = () => {
    if (course && course.sections) {
      const allSectionIds = course.sections.map((section) => section.id);
      setSelectedSections(allSectionIds);
    }
  };

  const deselectAllSections = () => {
    setSelectedSections([]);
  };

  return (
    <div className="course-popup-modal">
      <div className="course-popup-content">
        <div className="modal-header">
          <h2 className="course-popup-title">Duplicate Course</h2>
          <button
            className="close-button"
            onClick={onClose}
            disabled={isLoading || isSubmitting}
          >
            &times;
          </button>
        </div>

        <form className="course-popup-form" onSubmit={handleSubmit}>
          {/* Step indicator */}
          <div className="step-indicator">
            <div
              className={`step ${currentStep === 1 ? "active" : ""} ${
                courseName.trim() ? "completed" : ""
              }`}
            >
              1
            </div>
            <div className="step-line"></div>
            <div className={`step ${currentStep === 2 ? "active" : ""}`}>2</div>
          </div>

          {currentStep === 1 && (
            <div className="course-popup-form-group">
              <div className="course-popup-guide">
                <p className="guide-step">
                  Step 1: Name your duplicated course
                </p>
              </div>
              <label>Course Name</label>
              <input
                type="text"
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter name for the duplicated course..."
                className="course-popup-input"
                autoFocus
                disabled={isLoading || isSubmitting}
              />

              <div className="course-popup-nav-buttons">
                <button
                  type="button"
                  className="course-popup-next"
                  onClick={goToNextStep}
                  disabled={!courseName.trim() || isLoading || isSubmitting}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="course-popup-form-group">
              <div className="course-popup-guide">
                <p className="guide-step">
                  Step 2: Select sections to include in the duplicated course
                </p>
              </div>

              <div className="section-selector-controls">
                <button
                  type="button"
                  className="select-all-button"
                  onClick={selectAllSections}
                >
                  Select All
                </button>
                <button
                  type="button"
                  className="deselect-all-button"
                  onClick={deselectAllSections}
                >
                  Deselect All
                </button>
              </div>

              <div className="section-selector">
                {course && course.sections && course.sections.length > 0 ? (
                  <ul className="section-list">
                    {course.sections.map((section) => (
                      <li key={section.id} className="section-item">
                        <label className="section-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedSections.includes(section.id)}
                            onChange={() => toggleSectionSelection(section.id)}
                            disabled={isLoading || isSubmitting}
                          />
                          <span className="section-name">{section.name}</span>
                          {section.modules && (
                            <span className="module-count">
                              ({section.modules.length} module
                              {section.modules.length !== 1 ? "s" : ""})
                            </span>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-sections-message">
                    This course has no sections to duplicate.
                  </p>
                )}
              </div>

              <div className="course-popup-nav-buttons">
                <button
                  type="button"
                  className="course-popup-back"
                  onClick={goToPreviousStep}
                  disabled={isLoading || isSubmitting}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="course-popup-submit"
                  disabled={
                    isLoading ||
                    isSubmitting ||
                    !courseName.trim() ||
                    selectedSections.length === 0
                  }
                >
                  {isLoading || isSubmitting
                    ? "Duplicating Course..."
                    : "Duplicate Course"}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CoursePopUpDuplicate;
